import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { reservationApi } from '../../services/api';
import { A, card, STATUS_COLORS } from '../../adminTheme';

const AdminReservations = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [updating, setUpdating] = useState(null);

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const res = await reservationApi.getAll(filter ? { status: filter } : {});
      setReservations(res.data.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line
  useEffect(() => { fetchReservations(); }, [filter]);

  const handleStatus = async (id, status) => {
    setUpdating(id);
    try {
      await reservationApi.updateStatus(id, status);
      toast.success(`Reservation ${status}`);
      fetchReservations();
    } catch { toast.error('Update failed'); }
    finally { setUpdating(null); }
  };

  return (
    <div>
      <h1 style={{fontFamily:'Playfair Display,serif', fontSize:24, fontWeight:700, color:'#1a0f00', margin:'0 0 20px'}}>Reservations</h1>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <p style={{ color: A.text3, fontSize: 14 }}>{reservations.length} booking{reservations.length !== 1 ? 's' : ''}</p>
        <button onClick={fetchReservations} style={{ background: A.surface3, border: `1px solid ${A.border1}`, borderRadius: 10, padding: '8px 16px', color: A.text2, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}>
          ↻ Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {['', 'pending', 'confirmed', 'rejected', 'cancelled'].map(s => {
          const active = filter === s;
          const sc = s ? STATUS_COLORS[s] : null;
          return (
            <button key={s} onClick={() => setFilter(s)}
              style={{
                padding: '6px 16px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                fontFamily: 'inherit', cursor: 'pointer', textTransform: 'capitalize',
                background: active ? (sc ? sc.bg : A.accentSoft) : A.surface1,
                color: active ? (sc ? sc.color : A.accent) : A.text3,
                border: `1px solid ${active ? (sc ? sc.border : A.accentBorder) : A.border1}`,
              }}>
              {s || 'All'}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Array(4).fill(0).map((_, i) => (
            <div key={i} style={{ ...card({ padding: 20, height: 90 }), animation: 'pulse 1.5s infinite' }} />
          ))}
        </div>
      ) : reservations.length === 0 ? (
        <div style={{ ...card({ padding: '60px 24px' }), textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📅</div>
          <p style={{ color: A.text3, fontSize: 14 }}>No reservations {filter ? `with status "${filter}"` : 'yet'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reservations.map(res => {
            const sc = STATUS_COLORS[res.status] || STATUS_COLORS.pending;
            const date = new Date(res.reservation_date);
            return (
              <motion.div key={res.id} layout style={{ ...card({ padding: 20 }) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

                    {/* Date badge */}
                    <div style={{
                      width: 52, height: 52, borderRadius: 12, flexShrink: 0,
                      background: A.accentSoft, border: `1px solid ${A.accentBorder}`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: 18, fontWeight: 700, color: A.accent, lineHeight: 1 }}>{date.getDate()}</span>
                      <span style={{ fontSize: 10, color: A.text3 }}>{date.toLocaleDateString('en-IN', { month: 'short' })}</span>
                    </div>

                    {/* Info */}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                        <span style={{ color: A.text1, fontWeight: 600, fontSize: 15 }}>{res.name}</span>
                        <span style={{ display:'inline-flex', padding:'2px 10px', borderRadius:99, fontSize:11, fontWeight:600, textTransform:'capitalize', background:sc.bg, color:sc.color, border:`1px solid ${sc.border}` }}>{res.status}</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12, color: A.text3 }}>
                        <span>📞 {res.phone}</span>
                        <span>👥 {res.guests} guests</span>
                        <span>🕐 {res.reservation_time?.slice(0, 5)}</span>
                        {res.email && <span className="hidden sm:inline">✉️ {res.email}</span>}
                      </div>
                      {res.special_requests && (
                        <p style={{ color: A.text4, fontSize: 11, marginTop: 5, fontStyle: 'italic' }}>"{res.special_requests}"</p>
                      )}
                      {res.pre_order_items && res.pre_order_items.length > 0 && (
                        <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 10, background: '#fff4ee', border: '1px solid #f0d0b0' }}>
                          <p style={{ color: '#c96428', fontWeight: 700, fontSize: 11, marginBottom: 4 }}>🍽️ Pre-ordered:</p>
                          {res.pre_order_items.map((item, i) => (
                            <span key={i} style={{ display: 'inline-block', fontSize: 11, color: '#3d2010', marginRight: 8 }}>
                              {item.item_name} ×{item.quantity}
                            </span>
                          ))}
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#c96428', marginLeft: 4 }}>
                            · ₹{parseFloat(res.pre_order_total || 0).toFixed(0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {res.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button disabled={updating === res.id} onClick={() => handleStatus(res.id, 'confirmed')}
                        style={{ padding: '8px 16px', borderRadius: 10, border: `1px solid ${A.greenBorder}`, background: A.greenBg, color: A.green, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', opacity: updating === res.id ? 0.6 : 1 }}>
                        ✓ Confirm
                      </button>
                      <button disabled={updating === res.id} onClick={() => handleStatus(res.id, 'rejected')}
                        style={{ padding: '8px 16px', borderRadius: 10, border: `1px solid ${A.redBorder}`, background: A.redBg, color: A.red, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', opacity: updating === res.id ? 0.6 : 1 }}>
                        ✕ Reject
                      </button>
                    </div>
                  )}
                  {res.status === 'confirmed' && (
                    <button disabled={updating === res.id} onClick={() => handleStatus(res.id, 'cancelled')}
                      style={{ padding: '8px 16px', borderRadius: 10, border: `1px solid ${A.border1}`, background: A.surface3, color: A.text3, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}`}</style>
    </div>
  );
};

export default AdminReservations;
